import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { createPortal } from 'react-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useWindowDimensions from 'ui_system/utilities/useWindowDimensions';
import {
  ChartWrapper,
  OverflowContainer as Overflow,
  Watermark,
} from '../chart_styles/styles';
import { colors, fontSizes, shadows, shape, spacing } from '../utils';

const tooltipNames = {
  detractors_percentage: 'Detractors',
  nps: 'Net Promoter Score (NPS)',
  passives_percentage: 'Passives',
  promoters_percentage: 'Promoters',
};

const npsColors = {
  detractors_percentage: colors.nps.detractor,
  nps: colors.data.primary,
  passives_percentage: colors.nps.passive,
  promoters_percentage: colors.nps.promoter,
};

const defaultChartMaxHeight = 80;
const defaultRowLabelWidth = 250;

const ChartGroupWrapper = styled.div`
  align-items: center;
  color: ${colors.text.primary};
  display: flex;
  font-family: Soleil, sans-serif;
  height: ${(props) => (props.height ? `${props.height}px` : '100%')};
  justify-content: safe center;
  width: 100%;
`;

const ChartGroup = styled.div`
  padding-top: ${spacing.medium};
  position: relative;
`;

const OverflowContainer = styled(Overflow)`
  border: 1px solid ${colors.border};
  border-bottom: none;
  border-top: none;
`;

const ChartRow = styled.div`
  border-top: 1px solid ${colors.border};
  display: grid;
  grid-template-columns: ${(props) => props.rowLabelWidth}px auto;
  padding-top: ${spacing.xsmall};
  width: 100%;
`;

const RowLabel = styled.div`
  align-self: center;
  font-size: ${fontSizes.small};
  padding: ${spacing.none} ${spacing.medium};
  text-align: right;
`;

const StickyYAxisWrapper = styled.div`
  color: ${colors.text.secondary};
  font-size: ${fontSizes.small};
  height: ${(props) => props.height}px;
  width: 50px;
`;

const StickyYAxis = ({ label, maxHeight }) => {
  return (
    <StickyYAxisWrapper height={maxHeight}>
      <svg height={maxHeight} viewBox={`0 0 50 ${maxHeight}`} width="50">
        <text
          dominantBaseline="middle"
          fill={colors.text.secondary}
          fontSize={fontSizes.small}
          textAnchor="middle"
          transform="rotate(-90)"
          // x subtracted offset value (27) calculated by subtracting (the height of x axis / 2) - (top padding / 2)
          x={`-${maxHeight / 2 - 27}`}
          y="25"
        >
          {label}
        </text>
      </svg>
    </StickyYAxisWrapper>
  );
};
StickyYAxis.displayName = 'StickyYAxis';

const StickyXAxisWrapper = styled.div`
  border-top: 1px solid ${colors.border};
  padding-left: ${(props) => props.paddingLeft}px;
  width: 100%;
`;

const TooltipWrapper = styled.div`
  background: ${colors.white};
  box-shadow: ${shadows.primary};
  font-size: 13px;
  font-family: Soleil;
  left: ${(props) => props.x}px;
  position: absolute;
  top: ${(props) => props.y}px;
  width: ${(props) => props.tooltipWidth}px;
  z-index: 100;
`;

const TooltipData = styled.div`
  padding: ${spacing.medium} ${spacing.none} ${spacing.none} ${spacing.medium};
`;

const TooltipLabelValueGroup = styled.div`
  margin-top: ${spacing.xxsmall};
  padding-right: ${spacing.medium};
`;

const TooltipLabel = styled.div`
  color: ${colors.text.secondary};
  font-family: Soleil;
  font-size: 11px;
  font-weight: 400;
  margin: 0;
  margin-bottom: ${spacing.xxsmall};
  text-transform: uppercase;
`;

const TooltipValue = styled.div`
  color: ${colors.text.primary};
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  margin-bottom: ${(props) =>
    props.smallMargin ? spacing.small : spacing.medium};
`;

const TooltipValueAndBenchmark = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TooltipBenchmarkData = styled.div`
  background: ${colors.background.light};
  margin-bottom: ${spacing.medium};
  padding: ${spacing.xsmall};
`;

const TooltipBenchmarkLabel = styled.div`
  color: ${colors.text.icon};
  font-size: 9px;
  padding-bottom: ${spacing.xxsmall};
`;

const TooltipBenchmarkRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TooltipBenchmarkValue = styled.div`
  color: ${colors.text.primary};
  font-size: 13px;
  padding-right: ${spacing.small};
`;

const getDiffColor = (diff) => {
  if (diff === 0) {
    return colors.data.secondary;
  }
  if (diff < 0) {
    return colors.data.negative;
  }
  return colors.data.positive;
};

const TooltipBenchmarkDiff = styled.div`
  color: ${(props) => getDiffColor(props.diff)};
`;

const TooltipBreakdownData = styled.div`
  padding-right: ${spacing.medium};
`;

const TooltipLegendItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${(props) => (props.noMargin ? spacing.none : spacing.xsmall)};
`;

const TooltipLegendSquare = styled.div`
  background: ${(props) => props.color};
  border-radius: ${shape.borderRadius};
  height: ${spacing.medium};
  margin-right: ${spacing.xsmall};
  width: ${spacing.medium};
`;

const TooltipLegendSquareLabel = styled.div`
  align-items: center;
  display: flex;
  padding-right: ${spacing.medium};
`;

const TooltipLegendValue = styled.div`
  display: flex;
  font-weight: 600;
`;

const TooltipResponseCount = styled.p`
  color: ${colors.text.secondary};
  font-size: ${fontSizes.xsmall};
  padding: ${spacing.medium};
`;

const formatDiff = (diff, unit) => {
  const absDiff = Math.abs(diff);
  const formattedValue = `${Math.round(absDiff)}`;
  return `${diff < 0 ? '-' : '+'}${formattedValue}${unit}`;
};

const noBenchmarkData = (value) => value !== 0 && !value;

const CustomTooltip = ({
  active,
  benchmark,
  coordinates,
  dimensions,
  formatter,
  groupKey,
  groupName,
  label,
  name,
  payload,
}) => {
  const target = document.body;
  if (!active || !coordinates) return null;

  let tooltipWidth = 140;
  const isNPS = payload[0]?.unit === 'Net Promoter Score';
  // If there's benchmark data, the payload is returned in render order:
  // For Bars: [Bar/value, Line/compare_to_value]
  // For Lines: [Line/compare_to_value, Line/value]
  const load = isNPS && benchmark ? payload[1] : payload[0];
  // payload[1] will be undefined if there is no benchmark data
  const benchmarkLoad = isNPS && benchmark ? payload[0] : payload[1];
  const subPayload = load?.payload || {};
  const noData = load?.payload?.noData;
  const noNPSData = isNPS && noData;
  const diff =
    benchmarkLoad &&
    Math.round(load.value || 0) - Math.round(benchmarkLoad.value || 0);
  const totalResponses = subPayload.unit_count || 0;

  // Value maps
  const formattedValue = noNPSData ? 'No data' : formatter(load.value)[0];
  const formattedBenchmarkValue = noBenchmarkData(benchmarkLoad?.value)
    ? 'n/a'
    : formatter(benchmarkLoad.value)[0];
  const diffUnit = isNPS ? '' : '%';
  // Do not show diff if we do not have partner data or benchmark data
  const formattedDiff =
    noBenchmarkData(benchmarkLoad?.value) || noNPSData
      ? ''
      : formatDiff(diff, diffUnit);

  if (label && load.name) {
    tooltipWidth = 200;
  }
  const { width } = dimensions;
  let x = coordinates[0] + 10;
  const y = coordinates[1];
  if (x + tooltipWidth >= width) {
    x -= tooltipWidth + 20;
  }

  const TooltipPortal = (
    <TooltipWrapper tooltipWidth={tooltipWidth} x={x} y={y}>
      <TooltipData>
        {label && name && (
          <>
            <TooltipLabel>{name}</TooltipLabel>
            <TooltipValue>{label}</TooltipValue>
          </>
        )}
        <TooltipLabel>{groupName}</TooltipLabel>
        <TooltipValue smallMargin={!!benchmark}>{groupKey}</TooltipValue>
        <TooltipValueAndBenchmark>
          <TooltipLabelValueGroup>
            <TooltipLabel>{load.unit}</TooltipLabel>
            <TooltipValue>{formattedValue}</TooltipValue>
          </TooltipLabelValueGroup>
          {benchmark && (
            <TooltipBenchmarkData>
              <TooltipBenchmarkLabel>{benchmark}</TooltipBenchmarkLabel>
              <TooltipBenchmarkRow>
                <TooltipBenchmarkValue>
                  {formattedBenchmarkValue}
                </TooltipBenchmarkValue>
                <TooltipBenchmarkDiff diff={diff}>
                  {formattedDiff}
                </TooltipBenchmarkDiff>
              </TooltipBenchmarkRow>
            </TooltipBenchmarkData>
          )}
        </TooltipValueAndBenchmark>
        {isNPS && !noData && (
          <TooltipBreakdownData>
            {[
              'promoters_percentage',
              'passives_percentage',
              'detractors_percentage',
            ].map((category) => (
              <TooltipLegendItem
                key={category}
                noMargin={category === 'detractors_percentage'}
              >
                <TooltipLegendSquareLabel>
                  <TooltipLegendSquare color={npsColors[category]} />
                  {tooltipNames[category]}
                </TooltipLegendSquareLabel>
                <TooltipLegendValue>
                  {Math.round(subPayload[category])}%
                </TooltipLegendValue>
              </TooltipLegendItem>
            ))}
          </TooltipBreakdownData>
        )}
      </TooltipData>
      {isNPS && !noData && (
        <TooltipResponseCount>
          Based on {totalResponses} responses
        </TooltipResponseCount>
      )}
    </TooltipWrapper>
  );

  return createPortal(TooltipPortal, target);
};

const SimpleChart = ({
  barName,
  barSize,
  benchmarkProps,
  chartType,
  data,
  dataColor,
  dataKey,
  disableAnimations = false,
  domain,
  groupName,
  maxBarSize,
  name,
  tooltipFormatter,
  unit,
  width,
  yAxisProps,
}) => {
  const containerRef = useRef(null);
  const dimensions = useWindowDimensions();
  const [mouseCoordinates, setMouseCoordinates] = useState(null);
  const onMouseMove = (_, e) => {
    setMouseCoordinates([e.clientX, e.clientY]);
  };

  return (
    <ChartWrapper>
      <ComposedChart
        data={data}
        height={80}
        layout="horizontal"
        onMouseMove={onMouseMove}
        ref={containerRef}
        width={width}
      >
        <CartesianGrid
          fill={colors.background.light}
          horizontal={false}
          vertical={false}
        />
        <XAxis
          background={{
            fill: colors.graphLine,
          }}
          dataKey="timeLabel"
          height={5}
          label={false}
          scale="band"
          stroke={colors.graphLine}
          style={{
            color: colors.text.secondary,
          }}
          tick={false}
          tickLine={false}
        />
        <YAxis
          axisLine={false}
          background={{
            fill: colors.graphLine,
          }}
          domain={domain}
          label={{
            position: 'insideLeft',
            style: {
              fill: colors.text.secondary,
              fontSize: fontSizes.small,
              textAnchor: 'middle',
            },
          }}
          stroke={colors.graphLine}
          style={{
            color: colors.text.secondary,
          }}
          tick={{
            fill: colors.text.primary,
            fontSize: fontSizes.small,
          }}
          tickCount={yAxisProps.tickCount}
          tickFormatter={yAxisProps.tickFormatter}
          tickLine={false}
          width={yAxisProps.width}
          yAxisId={yAxisProps.yAxisId}
        />
        <Tooltip
          benchmark={benchmarkProps?.name}
          containerRef={containerRef}
          content={
            <CustomTooltip
              groupKey={name}
              groupName={groupName}
              name={barName}
            />
          }
          coordinates={mouseCoordinates}
          cursor={false}
          dimensions={dimensions}
          filterNull={false}
          formatter={tooltipFormatter}
        />
        {chartType === 'bar' && (
          <Bar
            barSize={barSize}
            dataKey={dataKey}
            fill={dataColor}
            isAnimationActive={!disableAnimations}
            legendType="none"
            maxBarSize={maxBarSize}
            name={name}
            unit={unit}
            yAxisId={yAxisProps.yAxisId}
          />
        )}
        {/* We want the benchmark to be above the bars, but underneath the line */}
        {benchmarkProps && (
          <Line {...benchmarkProps} isAnimationActive={!disableAnimations} />
        )}
        {chartType === 'line' && (
          <Line
            activeDot={false}
            connectNulls
            dataKey={dataKey}
            dot={{
              r: 2.5,
              stroke: dataColor,
              strokeWidth: 0,
            }}
            fill={dataColor}
            isAnimationActive={!disableAnimations}
            legendType="none"
            name={name}
            stroke={dataColor}
            strokeWidth={2.25}
            unit={unit}
            yAxisId={yAxisProps.yAxisId}
          />
        )}
      </ComposedChart>
    </ChartWrapper>
  );
};

export const BarChartGroup = ({
  benchmarkProps,
  chartOrder,
  data = {},
  disableAnimations = false,
  domain,
  downloadHeight,
  downloadRef,
  includeWatermark = false,
  maxBarSize,
  maxChartHeight = defaultChartMaxHeight,
  maxHeight,
  maxWidth,
  minWidth,
  rowLabelWidth = defaultRowLabelWidth,
  tooltipFormatter,
  xAxisProps = {},
  yAxisProps = {},
  ...chartProps
}) => {
  const wrapperRef = useRef(null);
  const dimensions = useWindowDimensions();
  const [chartWidth, setChartWidth] = useState(maxWidth - 100);
  const [barWidth, setBarWidth] = useState(chartProps.barSize);
  const firstDataset = data[Object.keys(data)[0]];
  const dataLength = firstDataset.length;

  useEffect(() => {
    const wrapperWidth = wrapperRef.current.clientWidth;
    let newWidth = wrapperWidth - 100 - rowLabelWidth;
    if (newWidth < minWidth) {
      newWidth = minWidth;
    } else if (newWidth > maxWidth - 100) {
      newWidth = maxWidth - 100;
    }
    let newBarWidth = Math.round(newWidth / dataLength) - 15;
    if (newBarWidth < 15) {
      newBarWidth = 15;
    } else if (newBarWidth > maxBarSize) {
      newBarWidth = maxBarSize;
    }

    setChartWidth(newWidth);
    setBarWidth(newBarWidth);
  }, [dataLength, dimensions, maxBarSize, maxWidth, minWidth, rowLabelWidth]);

  const chartYAxisProps = {
    ...yAxisProps,
    label: false,
    tick: false,
  };

  const charts = chartOrder.map((key) => {
    return (
      <ChartRow key={key} rowLabelWidth={rowLabelWidth}>
        <RowLabel>{key}</RowLabel>
        <SimpleChart
          {...chartProps}
          barSize={barWidth}
          benchmarkProps={benchmarkProps}
          data={data[key]}
          dimensions={dimensions}
          disableAnimations={disableAnimations}
          domain={domain}
          height={maxChartHeight}
          maxBarSize={maxBarSize}
          name={key}
          tooltipFormatter={tooltipFormatter}
          width={chartWidth}
          yAxisProps={yAxisProps}
        />
      </ChartRow>
    );
  });

  return (
    <ChartGroupWrapper ref={wrapperRef}>
      <ChartGroupWrapper ref={downloadRef} height={downloadHeight}>
        <StickyYAxis label={yAxisProps.label?.value} maxHeight={maxHeight} />
        <ChartGroup>
          {includeWatermark && <Watermark bottom={12} right={4} />}
          <OverflowContainer
            maxHeight={maxHeight - 86}
            maxWidth={chartWidth + rowLabelWidth}
            minWidth={minWidth + rowLabelWidth}
          >
            {charts}
          </OverflowContainer>
          <StickyXAxisWrapper
            paddingLeft={rowLabelWidth}
            maxWidth={chartWidth + rowLabelWidth}
          >
            <ComposedChart
              data={firstDataset}
              height={70}
              layout={chartProps.layout}
              margin={{ bottom: 35 }}
              width={chartWidth}
            >
              <XAxis {...xAxisProps} hide={false} scale="band" />
              <YAxis {...chartYAxisProps} tick={false} />
              <Bar
                dataKey={chartProps.dataKey}
                fill="transparent"
                isAnimationActive={false}
                legendType="none"
                name={chartProps.barName}
                yAxisId={chartYAxisProps.yAxisId}
              />
            </ComposedChart>
          </StickyXAxisWrapper>
        </ChartGroup>
      </ChartGroupWrapper>
    </ChartGroupWrapper>
  );
};

BarChartGroup.displayName = 'BarChartGroup';
